import React, { useRef, createContext, useContext, useState, useEffect } from 'react';
import * as Tone from 'tone';
import { DiscordSdkContext } from './DiscordSdkProvider';
import { convertDiscordImageUrl } from '../utils';
import { IS_STANDALONE } from '../constants';

const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const { isDiscord } = useContext(DiscordSdkContext);
  const [currentMusic, setCurrentMusic] = useState(null);
  const [musicVolume, setMusicVolume] = useState(IS_STANDALONE ? 0.20 : 0.0);
  const [sfxVolume, setSFXVolume] = useState(1.0);

  const [musicPlayers, setMusicPlayers] = useState({});
  const [sfxPlayers, setSFXPlayers] = useState({});

  const [currentMusicPlayer, setCurrentMusicPlayer] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize audio system
  // Trick for Strict Mode double mount
  const initialized = useRef({ isDiscord: false, init: false });
  useEffect(() => {
    if (isDiscord === initialized.current.isDiscord && initialized.current.init) {
      return;
    }
    initialized.current.isDiscord = isDiscord;
    initialized.current.init = true;
    const MUSIC_LOCATION = convertDiscordImageUrl('https://d9mwibab5tete.cloudfront.net/assets-prod/music', isDiscord);
    const SFX_LOCATION = convertDiscordImageUrl('https://d9mwibab5tete.cloudfront.net/assets-prod/sfx', isDiscord);
    // Wait for user interaction before starting audio
    const setupAudio = async () => {
      await Tone.start();
      console.log("Audio is ready");
    };

    const onLoadSFX = (sfxId) => {
      console.log(`SFX loaded: ${sfxId}`);
      if (sfxId === 'app_loading') {
        setIsInitialized(true);
      }
    };
    loadSFX({
      'app_loading':        `${SFX_LOCATION}/app_loading.mp3`,
      'button':             `${SFX_LOCATION}/button.mp3`,
      'menu_button':        `${SFX_LOCATION}/menu_button.mp3`,
      'loading':            `${SFX_LOCATION}/loading.mp3`,
      'prepare_for_battle': `${SFX_LOCATION}/prepare_for_battle.mp3`,
      'unequip':            `${SFX_LOCATION}/unequip.mp3`,
      'equip_weapon':       `${SFX_LOCATION}/equip_weapon.mp3`,
      'equip_armor':        `${SFX_LOCATION}/equip_armor.mp3`,
      'equip_object':       `${SFX_LOCATION}/equip_object.mp3`,
      'popup':              `${SFX_LOCATION}/popup.mp3`,
      'earn':               `${SFX_LOCATION}/earn.mp3`,
      'buy':                `${SFX_LOCATION}/buy.mp3`,
      'burger':             `${SFX_LOCATION}/burger.mp3`,
      'ramen':              `${SFX_LOCATION}/ramen.mp3`,
      'drink':              `${SFX_LOCATION}/drink.mp3`,
      'hover':              `${SFX_LOCATION}/hover.mp3`,
      'win1':               `${SFX_LOCATION}/win1.mp3`,
      'win2':               `${SFX_LOCATION}/win2.mp3`,
      'win3':               `${SFX_LOCATION}/win3.mp3`,
      'win4':               `${SFX_LOCATION}/win4.mp3`,
      'win5':               `${SFX_LOCATION}/win5.mp3`,
      'lose1':              `${SFX_LOCATION}/lose1.mp3`,
      'lose2':              `${SFX_LOCATION}/lose2.mp3`,
      'lose3':              `${SFX_LOCATION}/lose3.mp3`,
      'lose4':              `${SFX_LOCATION}/lose4.mp3`,
      'lose5':              `${SFX_LOCATION}/lose5.mp3`,
      'kitchen1':           `${SFX_LOCATION}/kitchen1.mp3`,
      'kitchen2':           `${SFX_LOCATION}/kitchen2.mp3`,
      'kitchen3':           `${SFX_LOCATION}/kitchen3.mp3`,
      'kitchen4':           `${SFX_LOCATION}/kitchen4.mp3`,
      'reward1':            `${SFX_LOCATION}/reward1.mp3`,
      'reward2':            `${SFX_LOCATION}/reward2.mp3`,
      'reward3':            `${SFX_LOCATION}/reward3.mp3`,
      'reward4':            `${SFX_LOCATION}/reward4.mp3`,
      'reward5':            `${SFX_LOCATION}/reward5.mp3`,
      'reward6':            `${SFX_LOCATION}/reward6.mp3`,
      'reward7':            `${SFX_LOCATION}/reward7.mp3`,
      'reward8':            `${SFX_LOCATION}/reward8.mp3`,
      'reward9':            `${SFX_LOCATION}/reward9.mp3`,
      'final1':             `${SFX_LOCATION}/final1.mp3`,
      'final2':             `${SFX_LOCATION}/final2.mp3`,
      'final3':             `${SFX_LOCATION}/final3.mp3`,
      'final4':             `${SFX_LOCATION}/final4.mp3`,
      'final5':             `${SFX_LOCATION}/final5.mp3`,
      'final6':             `${SFX_LOCATION}/final6.mp3`,
    }, onLoadSFX);

    loadMusic({
      'equip':   `${MUSIC_LOCATION}/equip.mp3`,
      'defeat':  `${MUSIC_LOCATION}/defeat.mp3`,
      'victory': `${MUSIC_LOCATION}/victory.mp3`,
      'battle_loading': `${MUSIC_LOCATION}/battle_loading.mp3`
    });

    // Attach event listener for first interaction
    const handleFirstInteraction = () => {
      setupAudio();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('keydown', handleFirstInteraction);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

  }, [isDiscord]);

  // Load music tracks
  const loadMusic = (musicTracks) => {
    const players = {};

    Object.entries(musicTracks).forEach(([trackId, url]) => {
      const player = new Tone.Player({
        url,
        autostart: trackId === 'equip',
        loop: true,
        fadeIn: 2,
        fadeOut: 2,
        volume: Tone.gainToDb(musicVolume),
        onload: () => console.log(`Music loaded: ${trackId}`),
      }).toDestination();

      players[trackId] = player;
    });

    setMusicPlayers(players);
  };

  // Load sound effects
  const loadSFX = (sfxTracks, onLoad) => {
    const players = {};

    Object.entries(sfxTracks).forEach(([sfxId, url]) => {
      const player = new Tone.Player({
        url,
        volume: Tone.gainToDb(sfxVolume),
        onload: () => onLoad(sfxId),
      }).toDestination();

      players[sfxId] = player;
    });

    setSFXPlayers(players);
  };

  useEffect(() => {
    if (!currentMusic) return;

    const trackId = currentMusic;
    const player = musicPlayers[trackId];
    Object.keys(musicPlayers).forEach((tid) => {
      if (tid !== trackId) {
        musicPlayers[tid].stop();
      }
    });

    //player.volume.value = -Infinity; // Start silent
    //player.volume.rampTo(isMusicMuted ? -Infinity : Tone.gainToDb(musicVolume), fadeTime);
    player.volume.value = Tone.gainToDb(musicVolume);
    try {
      player.start();
    } catch (err) {
      console.error(err);
    }
  }, [currentMusic]);

  const stopMusic = () => {
    Object.keys(musicPlayers).forEach((tid) => {
      musicPlayers[tid].stop();
    });
  };

  const playMusic = (trackId) => {
    if (!musicPlayers[trackId]) {
      console.error(`Music track ${trackId} not found`);
      return;
    }
    setCurrentMusic(trackId);
  };

  const playSFX = (sfxId) => {
    if (!sfxPlayers[sfxId]) {
      console.error(`SFX ${sfxId} not found`);
      return;
    }

    // Set volume and play
    const player = sfxPlayers[sfxId];
    player.volume.value = Tone.gainToDb(sfxVolume);
    try {
      player.start();
    } catch (err) {
      console.error(err);
    }
  };

  // Set music volume (0-1)
  const setMusicVolumeLevel = (level) => {
    setMusicVolume(Math.max(0, Math.min(1, level)));
    // Update all music players
    Object.values(musicPlayers).forEach(player => {
      player.volume.value = Tone.gainToDb(level);
    });
  };

  // Set SFX volume (0-1)
  const setSFXVolumeLevel = (level) => {
    setSFXVolume(Math.max(0, Math.min(1, level)));
    // Update all SFX players
    Object.values(sfxPlayers).forEach(player => {
      player.volume.value = Tone.gainToDb(level);
    });
  };

  // Provide audio functionality to the app
  return (
    <AudioContext.Provider
      value={{
        loadMusic,
        loadSFX,
        playMusic,
        stopMusic,
        playSFX,
        isInitialized,
        setMusicVolume: setMusicVolumeLevel,
        setSFXVolume: setSFXVolumeLevel,
        currentMusic,
        musicVolume,
        sfxVolume,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};
